// Whether the SetupIntent the card form was handed can still take a card.
//
// Stripe confirms a SetupIntent once. After that it is spent, and a second
// confirmation is refused with `setup_intent_unexpected_state` — which Stripe.js
// reports only as "A processing error occurred.", a message that says nothing
// of why, or of what to do.
//
// The server can hand one out spent: a card the bank confirmed in an earlier
// attempt, whose saving never finished (seen on staging, 2026-09-27). Nothing
// typed into the form can be saved through it, but the card already confirmed
// can: completing the SetupIntent is what the save was about to do next, and
// the server takes a completion as often as it is asked (billingApi.js).

/** How the card form goes on once a card can't be saved as it stands. */
export const Retry = Object.freeze({
   /** Complete the same SetupIntent again: Stripe has already confirmed its card. */
   COMPLETE: "complete",
   /** Start again with a new SetupIntent, for the same card or another one. */
   NEW_CARD: "newCard"
});

const UNEXPECTED_STATE = "setup_intent_unexpected_state";

// The statuses a SetupIntent can still be confirmed from.
const TAKES_A_CARD = new Set(["requires_payment_method", "requires_confirmation", "requires_action"]);

export const ALREADY_CONFIRMED_MESSAGE =
   "Your bank already confirmed the card you entered last time, but saving it didn't finish. Try again to finish saving that card.";

export const UNUSABLE_FORM_MESSAGE =
   "This card form can't be used any more, so the card wasn't saved. Please enter your card again.";

/**
 * How the card form goes on from a SetupIntent in `status`: null while it can
 * still take a card (or nothing is known of it), otherwise `{ retry, message }`.
 */
export function resolveSetupIntentStatus(status) {
   if (!status || TAKES_A_CARD.has(status)) return null;
   if (status === "succeeded") return { retry: Retry.COMPLETE, message: ALREADY_CONFIRMED_MESSAGE };
   return { retry: Retry.NEW_CARD, message: UNUSABLE_FORM_MESSAGE };
}

/**
 * The same, for a confirmation Stripe refused: `{ retry, message }` when it was
 * refused because the SetupIntent can't take a card any more, and null for any
 * other refusal — a declined card, a failed bank check — which the form shows
 * in Stripe's own words.
 */
export function resolveConfirmError(error) {
   if (error?.code !== UNEXPECTED_STATE) return null;

   return (
      resolveSetupIntentStatus(error.setup_intent?.status) ?? {
         retry: Retry.NEW_CARD,
         message: UNUSABLE_FORM_MESSAGE
      }
   );
}
